import { Box, Grid } from '@radix-ui/themes';
import { PitchDesignHelper } from 'classes/helpers/pitch-design.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTextInput } from 'components/common/form/text';
import { MachineContext } from 'contexts/machine.context';
import { DECIMALS_BREAKS, DECIMALS_SPEED, DECIMALS_SPIN } from 'enums/pitches';
import useLatest from 'hooks/useLatestRef';
import { t } from 'i18next';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { FC, useContext, useEffect, useState } from 'react';
import { usePitchDesignStore } from 'stores/use-pitch-design-store';

export const PDBreaksModeForm: FC = () => {
  const showCurrent = usePitchDesignStore((s) => s.showCurrent);
  const inputs = usePitchDesignStore((s) => s.inputs);
  const refBall = usePitchDesignStore((s) => s.refBall);
  const { speedMph, breaks, spins } = inputs;

  const { machine, unitFactors } = useContext(MachineContext);

  const [speedText, setSpeedText] = useState(
    (unitFactors.fromSpeedMph * speedMph).toFixed(DECIMALS_SPEED)
  );

  const latestSpeedMph = useLatest(speedMph);
  const latestBreaksIn = useLatest(breaks);

  const [wyText, setWyText] = useState(spins.wy.toFixed(DECIMALS_SPIN));

  // saved value is the opposite of what needs to be visible to the user (to get to hawkeye frame of ref)
  const [breaksXText, setBreaksXText] = useState(
    (-breaks.xInches * unitFactors.fromBreaksIn).toFixed(DECIMALS_BREAKS)
  );
  const [breaksZText, setBreaksZText] = useState(
    (breaks.zInches * unitFactors.fromBreaksIn).toFixed(DECIMALS_BREAKS)
  );

  // e.g. if we reload machine in the background from admin changes
  useEffect(() => {
    const speedValue = unitFactors.fromSpeedMph * latestSpeedMph.current;
    setSpeedText(speedValue.toFixed(DECIMALS_SPEED));

    const breakXValue =
      -unitFactors.fromBreaksIn * latestBreaksIn.current.xInches;
    setBreaksXText(breakXValue.toFixed(DECIMALS_BREAKS));

    const breakZValue =
      unitFactors.fromBreaksIn * latestBreaksIn.current.zInches;
    setBreaksZText(breakZValue.toFixed(DECIMALS_BREAKS));
  }, [unitFactors]);

  return (
    <ErrorBoundary identifier="PDMainForm">
      <Grid columns="4" gap={RADIX.FLEX.GAP.SM}>
        <Box>
          <CommonTextInput
            id="pitch-design-speed"
            label={t('pd.speed-units', {
              units: machine.isMetric ? 'kph' : 'mph',
            }).toString()}
            inputColor={
              PitchDesignHelper.validateBallSpeedMph(speedMph)
                ? undefined
                : RADIX.COLOR.WARNING
            }
            type="number"
            value={speedText}
            onChange={(v) => {
              setSpeedText(v ?? '');
            }}
            // user is entering values in their own units
            onNumericChange={(v) =>
              usePitchDesignStore
                .getState()
                .mergeInputs({ speedMph: v / unitFactors.fromSpeedMph })
            }
            hint_md={
              showCurrent
                ? t('pd.current-x', {
                    x: (unitFactors.fromSpeedMph * refBall.speed).toFixed(
                      DECIMALS_SPEED
                    ),
                  }).toString()
                : undefined
            }
          />
        </Box>

        <Box>
          <CommonTextInput
            id="pitch-design-spin-gyro"
            label="pd.gyro-spin"
            inputColor={
              PitchDesignHelper.validateSpin(spins.wy)
                ? undefined
                : RADIX.COLOR.WARNING
            }
            type="number"
            value={wyText}
            onChange={(v) => setWyText(v ?? '')}
            // NET SPIN - BREAKS MODE
            onNumericChange={(v) =>
              usePitchDesignStore
                .getState()
                .mergeInputs({ spins: { ...spins, wy: v } })
            }
            hint_md={
              showCurrent
                ? t('pd.current-x', {
                    x: refBall.wy.toFixed(DECIMALS_SPIN),
                  }).toString()
                : undefined
            }
          />
        </Box>
        <Box>
          <CommonTextInput
            id="pitch-design-vert-break"
            label={machine.isMetric ? 'pd.vert-break-cm' : 'pd.vert-break-in'}
            iconTooltip={PitchDesignHelper.VB_TOOLTIP_TEXT}
            inputColor={
              PitchDesignHelper.validateBreak(breaks.zInches)
                ? undefined
                : RADIX.COLOR.WARNING
            }
            type="number"
            value={breaksZText}
            onChange={(v) => setBreaksZText(v ?? '')}
            // user is entering values in their own units
            onNumericChange={(v) =>
              usePitchDesignStore.getState().mergeInputs({
                breaks: { ...breaks, zInches: v / unitFactors.fromBreaksIn },
              })
            }
            hint_md={
              showCurrent
                ? t('pd.current-x', {
                    x: (
                      (refBall.breaks?.zInches ?? 0) * unitFactors.fromBreaksIn
                    ).toFixed(DECIMALS_BREAKS),
                  }).toString()
                : undefined
            }
          />
        </Box>
        <Box>
          <CommonTextInput
            id="pitch-design-hor-break"
            label={machine.isMetric ? 'pd.hor-break-cm' : 'pd.hor-break-in'}
            iconTooltip={PitchDesignHelper.HB_TOOLTIP_TEXT}
            inputColor={
              PitchDesignHelper.validateBreak(breaks.xInches)
                ? undefined
                : RADIX.COLOR.WARNING
            }
            type="number"
            value={breaksXText}
            onChange={(v) => setBreaksXText(v ?? '')}
            // user is entering values in their own units
            onNumericChange={(v) =>
              usePitchDesignStore.getState().mergeInputs({
                breaks: { ...breaks, xInches: -v / unitFactors.fromBreaksIn },
              })
            }
            hint_md={
              showCurrent
                ? t('pd.current-x', {
                    x: (
                      -(refBall.breaks?.xInches ?? 0) * unitFactors.fromBreaksIn
                    ).toFixed(DECIMALS_BREAKS),
                  }).toString()
                : undefined
            }
          />
        </Box>
      </Grid>
    </ErrorBoundary>
  );
};
